import { HxActiveSetEvent, HxActiveSetEventType, HxObject, SessionId } from '@outlook/hxcore_web';
import { runInHxSession } from './runInHxSession';
import { getHxInstance } from './getHxInstance';
import { waitForHxChange } from './waitForHxChange';

/**
 * Wait for the given predicate to be true for the given hxObject and return the version of the hxObject
 * which satisfies the predicate.
 */
export function waitForHxObjectChange<T extends HxObject>(
    hxObject: T,
    predicate: (hxObject: T) => boolean,
    timeout: number
): Promise<T> {
    return runInHxSession(async (sessionId: SessionId) => {
        const objectId = hxObject.objectId;
        // Hold on to the object to make sure we are getting change notifications for it.
        hxObject = (await getHxInstance().activeSet.getObject(objectId, sessionId)) as T;

        if (predicate(hxObject)) {
            return Promise.resolve(hxObject);
        }

        const evaluator = (event: HxActiveSetEvent): T | undefined => {
            if (
                event.type === HxActiveSetEventType.ObjectChanged &&
                event.object.objectId.isEqual(objectId) &&
                predicate(event.object as T)
            ) {
                return event.object as T;
            }
            return undefined;
        };
        return waitForHxChange(evaluator, timeout);
    });
}
